/**
 * 视频模型能力配置
 *
 * 用于判断模型是否支持全能参考等能力，避免在业务代码中硬编码 model_key。
 * Werydance / Seedance 2.0、2.5 与 Hailuo 03 支持全能参考。
 */

const ALMIGHTY_REFERENCE_VERSION_KEYS = new Set([
    'WERYDANCE_2_5',
    'WERYDANCE_2_5_OS',
    'SEEDANCE_2_5',
    'SEEDANCE_2_5_OS',
    'WERYDANCE_2_0',
    'WERYDANCE_2_0_OS',
    'WERYDANCE_2_0_MINI',
    'WERYDANCE_2_0_MINI_OS',
    'WERYDANCE_2_0_FAST',
    'WERYDANCE_2_0_FAST_OS',
    'SEEDANCE_2_0',
    'SEEDANCE_2_0_OS',
    'SEEDANCE_2_0_MINI',
    'SEEDANCE_2_0_MINI_OS',
    'SEEDANCE_2_0_FAST',
    'SEEDANCE_2_0_FAST_OS',
    'HAILUO_03',
])

const DURATION_SLIDER_MODEL_KEYS = new Set([
    'RUNWAY',
    'RUNWAY_4_5',
    'GOOGLE_GEMINI_OMNI_FLASH',
    ...ALMIGHTY_REFERENCE_VERSION_KEYS,
])

/** 已知不支持全能参考的版本（当前无） */
const ALMIGHTY_REFERENCE_VERSION_BLOCKLIST = new Set<string>([])

const normalizeCapabilityKey = (modelKey: string): string => {
    return modelKey.trim().toLowerCase().replace(/[\s._-]+/g, '')
}

const HAILUO_03_CAPABILITY_KEYS = new Set(['hailuo03', 'minimaxh3'])

/**
 * 判断模型/版本是否支持全能参考
 * @param modelKey model_key 字符串（如 WERYDANCE_2_5、SEEDANCE_2_0、HAILUO_03）
 */
export function isAlmightyReferenceSupported(modelKey?: string): boolean {
    if (!modelKey || typeof modelKey !== 'string') return false
    const key = modelKey.trim()
    if (ALMIGHTY_REFERENCE_VERSION_BLOCKLIST.has(key)) return false
    if (ALMIGHTY_REFERENCE_VERSION_KEYS.has(key)) return true
    const lower = normalizeCapabilityKey(key)
    return (
        lower === 'werydance25' ||
        lower === 'werydance25os' ||
        lower === 'werydance20' ||
        lower === 'werydance20os' ||
        lower === 'werydance20mini' ||
        lower === 'werydance20minios' ||
        lower === 'werydance20fast' ||
        lower === 'werydance20fastos' ||
        lower === 'werydance2' ||
        lower === 'werydance2mini' ||
        lower === 'seedance25' ||
        lower === 'seedance25os' ||
        lower === 'seedance20' ||
        lower === 'seedance20os' ||
        lower === 'seedance20mini' ||
        lower === 'seedance20minios' ||
        lower === 'seedance20fast' ||
        lower === 'seedance20fastos' ||
        lower === 'seedance2' ||
        lower === 'seedance2mini' ||
        HAILUO_03_CAPABILITY_KEYS.has(lower)
    )
}

export function isDance25Model(modelKey?: string): boolean {
    if (!modelKey || typeof modelKey !== 'string') return false
    const key = normalizeCapabilityKey(modelKey)
    return key === 'seedance25' || key === 'seedance25os' ||
        key === 'werydance25' || key === 'werydance25os'
}

export function isDance20Model(modelKey?: string): boolean {
    if (!modelKey || typeof modelKey !== 'string') return false
    const key = normalizeCapabilityKey(modelKey)
    return (
        key === 'seedance20' ||
        key === 'seedance20os' ||
        key === 'seedance20mini' ||
        key === 'seedance20minios' ||
        key === 'seedance20fast' ||
        key === 'seedance20fastos' ||
        key === 'werydance20' ||
        key === 'werydance20os' ||
        key === 'werydance20mini' ||
        key === 'werydance20minios' ||
        key === 'werydance20fast' ||
        key === 'werydance20fastos'
    )
}

export function isHailuo03Model(modelKey?: string): boolean {
    if (!modelKey || typeof modelKey !== 'string') return false
    return HAILUO_03_CAPABILITY_KEYS.has(normalizeCapabilityKey(modelKey))
}

type VideoModelCapability = {
    model_key?: string
    init_model?: string
    category?: { code?: string }
    aspect_ratios?: string[]
    model_version_list?: Array<{ model_key?: string }>
}

const getVideoModelCapabilityKeys = (
    model: VideoModelCapability | null | undefined
) => {
    if (!model) return []
    return [
        model.model_key,
        model.init_model,
        model.category?.code,
        ...(Array.isArray(model.model_version_list)
            ? model.model_version_list.map(version => version?.model_key)
            : []),
    ].filter((modelKey): modelKey is string => typeof modelKey === 'string')
}

export function isDance25ModelConfig(
    model: VideoModelCapability | null | undefined
): boolean {
    return getVideoModelCapabilityKeys(model).some(isDance25Model)
}

/**
 * 切换模型时只根据目标模型配置判断是否能保留全能参考状态。
 * 不能读取当前选中的 model/version，否则从全能参考模型切到普通模型时
 * 会把旧状态误判为目标模型能力。
 */
export function shouldPreserveAlmightyReferenceModeForModel(
    model: VideoModelCapability | null | undefined,
    imageType?: string,
    frameEndMode?: boolean
): boolean {
    if (!model || imageType !== 'multiple_frames' || frameEndMode) return false

    return getVideoModelCapabilityKeys(model).some(isAlmightyReferenceSupported)
}

const findVideoModelCapability = (
    models: unknown,
    modelKey?: string
): VideoModelCapability | undefined => {
    if (!Array.isArray(models) || !modelKey) return undefined
    const normalizedKey = normalizeCapabilityKey(modelKey)
    return models.find((model: VideoModelCapability) =>
        [model?.model_key, model?.category?.code].some(candidate =>
            typeof candidate === 'string' &&
            (normalizeCapabilityKey(candidate) === normalizedKey ||
                (isHailuo03Model(candidate) && isHailuo03Model(modelKey)))
        )
    )
}

/**
 * H3 全能参考与文生视频共享具体比例；`adaptive` 只属于全能参考。
 * H3 的图生配置按协议返回空比例，因此仅为 H3 从文生分组补齐具体比例。
 */
export function resolveAlmightyReferenceAspectRatios(
    modelData: any,
    modelKey?: string,
    imageToVideoRatios: unknown = []
): string[] {
    const configuredRatios = Array.isArray(imageToVideoRatios)
        ? imageToVideoRatios.filter((ratio): ratio is string => typeof ratio === 'string' && !!ratio)
        : []
    if (!isHailuo03Model(modelKey)) return configuredRatios
    if (configuredRatios.includes('adaptive')) return configuredRatios

    const textModel = findVideoModelCapability(
        modelData?.video_models?.text_to_video,
        modelKey
    )
    const concreteRatios = Array.isArray(textModel?.aspect_ratios)
        ? textModel.aspect_ratios.filter(ratio => ratio !== 'adaptive')
        : []

    return Array.from(new Set(['adaptive', ...configuredRatios, ...concreteRatios]))
}

/**
 * 判断模型/版本是否使用时长滑块。
 * 仅控制 UI 展示，不代表模型支持全能参考。
 */
export function isDurationSliderSupported(modelKey?: string): boolean {
    if (!modelKey || typeof modelKey !== 'string') return false
    const key = modelKey.trim()
    if (DURATION_SLIDER_MODEL_KEYS.has(key)) return true

    const lower = normalizeCapabilityKey(key)
    return (
        isAlmightyReferenceSupported(key) ||
        lower === 'runway' ||
        lower === 'runway45' ||
        lower === 'googlegeminiomniflash'
    )
}
